import { supabase } from '../lib/supabase';

interface RelatedUser {
  id: string;
  username?: string;
  full_name?: string;
  avatar_url?: string;
  is_premium?: boolean;
}

interface RelatedPost {
  id: string;
  title?: string;
  media_files?: Array<{ url: string; type: string }>;
}

interface RelatedComment {
  id: string;
  content?: string;
}

interface RelatedCollaboration {
  id: string;
  title?: string;
}

interface RelatedEvent {
  id: string;
  title?: string;
}

export interface Notification {
  id: string;
  user_id: string;
  type: 'like' | 'comment' | 'follow' | 'collaboration' | 'sale' | 'system' | 'event';
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  related_user_id?: string;
  related_post_id?: string;
  related_comment_id?: string;
  related_collaboration_id?: string;
  related_event_id?: string;
  metadata: Record<string, unknown>;
  related_user?: RelatedUser;
  related_post?: RelatedPost;
  related_comment?: RelatedComment;
  related_collaboration?: RelatedCollaboration;
  related_event?: RelatedEvent;
}

export const notificationsService = {
  // ---------- Email Notifications (Simulation) ----------
  async sendEmailNotification(to: string, subject: string, htmlBody: string) {
    try {
      // Check if email notifications are enabled in settings
      const savedSettings = localStorage.getItem('notification-settings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        if (!settings.email_notifications) {
          console.log('📧 Email notification skipped (disabled in settings):', subject);
          return { sent: false, reason: 'disabled_in_settings' };
        }
      }

      console.log('📧 Sending email via Edge Function...');
      
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: { to, subject, html: htmlBody }
      });

      if (error) {
        console.error('Edge Function error:', error);
        throw error;
      }

      console.log('📧 Email sent successfully:', data);
      
      // Log to localStorage for verification
      const emailLog = JSON.parse(localStorage.getItem('email_logs') || '[]');
      emailLog.unshift({
        id: data?.messageId || crypto.randomUUID(),
        to,
        subject,
        timestamp: new Date().toISOString(),
        status: 'sent',
        provider: 'edge_function'
      });
      // Keep only last 50 logs
      if (emailLog.length > 50) emailLog.pop();
      localStorage.setItem('email_logs', JSON.stringify(emailLog));

      return { sent: true, messageId: data?.messageId };
    } catch (error) {
      console.error('Error sending email:', error);
      // Fallback to simulation/logging if edge function fails (for dev/demo purposes)
      // or just return failure.
      return { sent: false, error };
    }
  },

  async checkEmailServiceStatus() {
    const emailLog = JSON.parse(localStorage.getItem('email_logs') || '[]');
    return {
      status: 'active_simulation',
      provider: 'console_logger',
      totalSent: emailLog.length,
      lastEmail: emailLog[0] || null
    };
  },

  // ---------- Retrieval ----------
  async getUserNotifications(options?: { unreadOnly?: boolean; type?: string; limit?: number; offset?: number }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('notifications')
      .select(`
        *,
        related_user:profiles!notifications_related_user_id_fkey (id, username, full_name, avatar_url, is_premium),
        related_post:posts!notifications_related_post_id_fkey (id, title, media_files (url, type)),
        related_comment:comments!notifications_related_comment_id_fkey (id, content),
        related_collaboration:collaborations!notifications_related_collaboration_id_fkey (id, title),
        related_event:events!notifications_related_event_id_fkey (id, title)
      `)
      .eq('user_id', user.id);

    if (options?.unreadOnly) query = query.eq('is_read', false);
    if (options?.type) query = query.eq('type', options.type);
    query = query.order('created_at', { ascending: false });
    if (options?.limit) query = query.limit(options.limit);
    if (options?.offset) query = query.range(options.offset, options.offset + (options.limit || 20) - 1);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  // ---------- Mutations ----------
  async markAsRead(notificationId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, updated_at: new Date().toISOString() })
      .eq('id', notificationId)
      .eq('user_id', user.id);
    if (error) throw error;
  },

  async markAllAsRead() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const { error } = await supabase.rpc('mark_all_notifications_read', { target_user_id: user.id });
    if (error) throw error;
  },

  async deleteNotification(notificationId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const { error } = await supabase.from('notifications').delete().eq('id', notificationId).eq('user_id', user.id);
    if (error) throw error;
  },

  async getUnreadCount() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('is_read', false);
    if (error) throw error;
    return count || 0;
  },

  // ---------- Creation ----------
  async createSystemNotification(userId: string, title: string, message: string, metadata?: Record<string, unknown>) {
    const { data, error } = await supabase
      .from('notifications')
      .insert({ user_id: userId, type: 'system', title, message, metadata: metadata || {} })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async createSaleNotification(sellerId: string, buyerName: string, postTitle: string, amount: number, postId?: string, buyerId?: string) {
    const title = '¡Nueva venta realizada!';
    // Ensure amount is a number before calling toFixed
    const safeAmount = Number(amount) || 0;
    const message = `Has vendido "${postTitle}" a ${buyerName} por ${safeAmount.toFixed(2)}€. Los fondos estarán disponibles en 20 días.`;
    
    // 1. Create in-app notification for Seller
    const { data, error } = await supabase
      .from('notifications')
      .insert({
        user_id: sellerId,
        type: 'sale',
        title,
        message,
        related_post_id: postId,
        related_user_id: buyerId,
        metadata: {
          post_title: postTitle,
          buyer_name: buyerName,
          amount: safeAmount
        }
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating sale notification:', error);
      throw error;
    }
    return data;
  },

  async createPurchaseNotification(buyerId: string, sellerName: string, postTitle: string, amount: number, postId?: string, sellerId?: string) {
    const title = '¡Compra realizada con éxito!';
    const safeAmount = Number(amount) || 0;
    const message = `Has comprado "${postTitle}" de ${sellerName} por ${safeAmount.toFixed(2)}€.`;

    // Create in-app notification for Buyer
    const { data, error } = await supabase
      .from('notifications')
      .insert({
        user_id: buyerId,
        type: 'sale', // Using 'sale' type for icon/filtering consistency, or could add 'purchase' type
        title,
        message,
        related_post_id: postId,
        related_user_id: sellerId,
        metadata: {
          post_title: postTitle,
          seller_name: sellerName,
          amount: safeAmount,
          is_purchase: true
        }
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating purchase notification:', error);
    }
    return data;
  },

  async createLikeNotification(likerId: string, authorId: string, postId: string) {
    const [{ data: liker }, { data: author }] = await Promise.all([
      supabase.from('profiles').select('email, username').eq('id', likerId).maybeSingle(),
      supabase.from('profiles').select('email, username').eq('id', authorId).maybeSingle(),
    ]);
    if (!liker?.email || !author?.email) {
      console.warn('Missing email for like notification', { likerId, authorId });
      return;
    }
    const subject = `${liker.username || 'Alguien'} ha gustado tu publicación`;
    const htmlBody = `<p>Hola ${author.username || ''},</p>
      <p>¡${liker.username || 'Alguien'} ha dado "Me gusta" a tu publicación! 🎉</p>
      <p>Puedes verla aquí: <a href="${process.env.VITE_PUBLIC_SITE_URL}/posts/${postId}">Ver publicación</a></p>
      <p>Gracias por compartir tu arte en SoArtistic.</p>`;
    
    // Use the simulation method
    await this.sendEmailNotification(author.email, subject, htmlBody);
  },



  async createEventNotification(userId: string, eventTitle: string, type: 'reminder' | 'cancelled' | 'updated', message: string) {
    const { data, error } = await supabase
      .from('notifications')
      .insert({
        user_id: userId,
        type: 'event',
        title: `Evento: ${eventTitle}`,
        message,
        metadata: { action: type, event_title: eventTitle },
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // ---------- Real‑time ----------
  subscribeToNotifications(userId: string, callback: (notification: Notification) => void) {
    return supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        async (payload) => {
          const { data } = await supabase
            .from('notifications')
            .select(`
              *,
              related_user:profiles!notifications_related_user_id_fkey (id, username, full_name, avatar_url, is_premium),
              related_post:posts!notifications_related_post_id_fkey (id, title, media_files (url, type))
            `)
            .eq('id', payload.new.id)
            .single();
          if (data) callback(data);
        }
      )
      .subscribe();
  },

  // ---------- Batch ----------
  async deleteMultipleNotifications(notificationIds: string[]) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const { error } = await supabase.from('notifications').delete().eq('user_id', user.id).in('id', notificationIds);
    if (error) throw error;
  },

  async markMultipleAsRead(notificationIds: string[]) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, updated_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .in('id', notificationIds);
    if (error) throw error;
  },

  async recordModerationModalView(notificationId: string, metadata: Record<string, unknown>) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const updatedMetadata = {
      ...(metadata || {}),
      moderation_modal_viewed: true,
      moderation_modal_viewed_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('notifications')
      .update({ metadata: updatedMetadata, updated_at: new Date().toISOString() })
      .eq('id', notificationId)
      .eq('user_id', user.id);

    if (error) throw error;
    return updatedMetadata;
  },

  async getNotificationsByType() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const { data, error } = await supabase.from('notifications').select('type, is_read').eq('user_id', user.id);
    if (error) throw error;
    const grouped = data?.reduce((acc, n) => {
      const type = n.type;
      if (!acc[type]) acc[type] = { total: 0, unread: 0 };
      acc[type].total++;
      if (!n.is_read) acc[type].unread++;
      return acc;
    }, {} as Record<string, { total: number; unread: number }>);
    return grouped || {};
  },
};
